import { useEffect, useState } from "react";
import { customFetch } from "@workspace/api-client-react";
import { t } from "@workspace/strings/web";
import { newsConfig, newsRotationIndex } from "@/player/lib/widgets";

interface FeedItem {
  title: string;
  publishedAt: string | null;
  source: string | null;
}

interface FeedResponse {
  items: FeedItem[];
  fetchedAt: string;
}

/** Matches the server-side cache TTL — no point polling faster. */
const REFRESH_MS = 5 * 60 * 1000;

/**
 * Rotating headline board. The feed comes through the device API proxy
 * (`GET /api/device/signage-feed/:id` — SSRF-guarded, cached server-side);
 * the player never fetches customer URLs directly.
 */
export function NewsWidget({
  signageContentId,
  config,
  previewItems,
}: {
  signageContentId: string;
  config: Record<string, unknown> | null;
  /** Editor preview: render these instead of fetching (device auth is absent there). */
  previewItems?: FeedItem[];
}) {
  const cfg = newsConfig(config);
  const [items, setItems] = useState<FeedItem[] | null>(previewItems ?? null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (previewItems) {
      setItems(previewItems);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await customFetch<FeedResponse>(
          `/api/device/signage-feed/${signageContentId}`,
        );
        if (!cancelled) setItems(res.items);
      } catch {
        // Keep showing the previous headlines (or the loading state) — the
        // server already serves stale-on-error, so a hard failure here means
        // the feed is truly unavailable.
        if (!cancelled) setItems((prev) => prev ?? []);
      }
    };
    void load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [signageContentId, previewItems]);

  useEffect(() => {
    const tick = () => {
      setIndex(newsRotationIndex(items?.length ?? 0, cfg.rotateSec, Date.now()));
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [items, cfg.rotateSec]);

  if (items === null) {
    return (
      <div
        className="flex h-full w-full items-center justify-center bg-background text-muted-foreground text-[2.5vw]"
        data-testid="widget-news"
      >
        {t("player.widgets.news.loading")}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div
        className="flex h-full w-full items-center justify-center bg-background text-muted-foreground text-[2.5vw]"
        data-testid="widget-news"
      >
        {t("player.widgets.news.empty")}
      </div>
    );
  }

  const item = items[Math.min(index, items.length - 1)];

  return (
    <div
      className="flex h-full w-full flex-col justify-center bg-background px-[7vw]"
      data-testid="widget-news"
    >
      {item.source && (
        <div className="mb-[2vh] text-[1.8vw] uppercase tracking-widest text-muted-foreground">
          {item.source}
        </div>
      )}
      {/* Key remount fades each headline in without a timer of its own. */}
      <div
        key={`${index}-${item.title}`}
        className="text-[4.5vw] font-semibold leading-tight text-foreground animate-in fade-in duration-500"
      >
        {item.title}
      </div>
      <div className="mt-[4vh] flex items-center gap-[0.8vw]">
        {items.map((_, i) => (
          <span
            key={i}
            className={
              i === Math.min(index, items.length - 1)
                ? "h-[0.5vh] w-[2.5vw] rounded-full bg-foreground/80"
                : "h-[0.5vh] w-[1vw] rounded-full bg-muted-foreground/30"
            }
          />
        ))}
      </div>
    </div>
  );
}
